import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { clienteName, fmtDate, km } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Targa } from "../components/Targa";
import { TextField, SelectField, Field } from "../components/Field";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY = { cliente_id: "", targa: "", telaio: "", marca: "", modello: "", versione: "", alimentazione: "", km: 0, data_immatricolazione: "", data_vendita: "", usato: true, note: "" };
const ALIM = ["Benzina", "Diesel", "GPL", "Metano", "Ibrida", "Elettrica"].map((v) => ({ value: v, label: v }));

const VeicoloDialog = ({ open, onClose, initial, clienti, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      payload.km = Number(payload.km) || 0;
      initial?.id ? await api.put(`/veicoli/${initial.id}`, payload) : await api.post("/veicoli", payload);
      toast.success("Veicolo salvato"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="veicolo-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? "Modifica veicolo" : "Nuovo veicolo"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label="Cliente (proprietario) *" value={f.cliente_id} onChange={set("cliente_id")} testId="veicolo-cliente-select" className="sm:col-span-2"
            options={clienti.map((c) => ({ value: c.id, label: clienteName(c) }))} />
          <TextField label="Targa *" value={f.targa} onChange={(v) => set("targa")(v.toUpperCase())} testId="veicolo-targa-input" placeholder="AB123CD" className="[&_input]:font-mono" />
          <TextField label="Telaio (VIN)" value={f.telaio} onChange={(v) => set("telaio")(v.toUpperCase())} testId="veicolo-telaio-input" maxLength={17} className="[&_input]:font-mono" />
          <TextField label="Marca *" value={f.marca} onChange={set("marca")} testId="veicolo-marca-input" />
          <TextField label="Modello *" value={f.modello} onChange={set("modello")} testId="veicolo-modello-input" />
          <TextField label="Versione" value={f.versione} onChange={set("versione")} testId="veicolo-versione-input" />
          <SelectField label="Alimentazione" value={f.alimentazione} onChange={set("alimentazione")} options={ALIM} testId="veicolo-alimentazione-select" />
          <TextField label="Chilometri" type="number" value={f.km} onChange={set("km")} testId="veicolo-km-input" />
          <TextField label="Data immatricolazione" type="date" value={f.data_immatricolazione} onChange={set("data_immatricolazione")} testId="veicolo-immatricolazione-input" />
          <TextField label="Data vendita / consegna" type="date" value={f.data_vendita} onChange={set("data_vendita")} testId="veicolo-vendita-input" />
          <Field label="Veicolo usato">
            <div className="flex h-10 items-center gap-2"><Switch checked={f.usato} onCheckedChange={set("usato")} data-testid="veicolo-usato-switch" /><span className="text-sm">{f.usato ? "Usato" : "Nuovo"}</span></div>
          </Field>
          <Field label="Note" className="sm:col-span-2"><Textarea value={f.note} onChange={(e) => set("note")(e.target.value)} data-testid="veicolo-note-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="veicolo-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={!f.cliente_id || !f.targa || !f.marca || !f.modello} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="veicolo-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Veicoli() {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/veicoli", { params: { q } }).then((r) => setItems(r.data));
  useEffect(() => { api.get("/clienti").then((r) => setClienti(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const remove = async (v) => {
    if (!window.confirm(`Eliminare il veicolo ${v.targa}?`)) return;
    try { await api.delete(`/veicoli/${v.id}`); toast.success("Veicolo eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Veicoli" subtitle="Parco veicoli venduti e in garanzia">
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="new-veicolo-btn"><Plus className="mr-1 h-4 w-4" /> Nuovo veicolo</Button>
      </PageHeader>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Cerca targa, telaio, marca, modello…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-9" data-testid="veicoli-search-input" />
      </div>
      <div className="overflow-x-auto rounded-md border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Targa</TableHead><TableHead>Veicolo</TableHead><TableHead>Telaio</TableHead><TableHead>Km</TableHead><TableHead>Immatricolazione</TableHead><TableHead>Proprietario</TableHead><TableHead className="w-24" />
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Nessun veicolo</TableCell></TableRow>}
            {items.map((v) => (
              <TableRow key={v.id} data-testid={`veicolo-row-${v.id}`}>
                <TableCell><Targa value={v.targa} /></TableCell>
                <TableCell><div className="font-semibold">{v.marca} {v.modello}</div><div className="text-xs text-muted-foreground">{v.versione} {v.alimentazione && `· ${v.alimentazione}`} · {v.usato ? "Usato" : "Nuovo"}</div></TableCell>
                <TableCell className="font-mono text-xs">{v.telaio || "—"}</TableCell>
                <TableCell className="text-sm">{km(v.km)}</TableCell>
                <TableCell className="text-sm">{fmtDate(v.data_immatricolazione)}</TableCell>
                <TableCell className="text-sm">{clienteName(v.cliente)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => { setEdit(v); setOpen(true); }} data-testid={`edit-veicolo-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(v)} data-testid={`delete-veicolo-${v.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <VeicoloDialog open={open} onClose={() => setOpen(false)} initial={edit} clienti={clienti} onSaved={load} />
    </div>
  );
}
